/**
 * Apply List Settings to Column Definitions
 * 
 * This helper enforces that only columns in `visibleFieldOrder` are rendered,
 * and creates generic columns for meta fields that don't have bespoke columns.
 * 
 * This makes List Settings the single source of truth for column visibility.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { StatusBadge } from "@/components/ui/status-badge";
import { formatDate, formatDateTime } from "@/lib/format";

type AvailableField = {
  fieldname: string;
  label: string;
  fieldtype?: string;
  required?: boolean;
};

/**
 * Apply list settings visibility and ordering to columns
 * 
 * @param baseColumns - Original column definitions (from schema or bespoke factory)
 * @param visibleFieldOrder - Ordered list of fieldnames that should be visible
 * @param availableFields - Meta fields available for generic column generation
 * @returns Filtered and ordered columns, with generic columns for meta-only fields
 */
export function applyListSettingsToColumns<T = unknown>(
  baseColumns: ColumnDef<T, unknown>[],
  visibleFieldOrder: string[],
  availableFields: AvailableField[]
): ColumnDef<T, unknown>[] {
  if (!visibleFieldOrder || !visibleFieldOrder.length) {
    // No list settings: return base columns as-is
    return baseColumns;
  }

  const getColumnId = (col: ColumnDef<T, unknown>): string | null => {
    if (typeof col.id === "string" && col.id) return col.id;
    const accessorKey = (col as { accessorKey?: unknown }).accessorKey;
    return typeof accessorKey === "string" && accessorKey ? accessorKey : null;
  };

  // Create map of fieldname -> column from base columns
  const byId = new Map<string, ColumnDef<T, unknown>>(
    baseColumns
      .map((col) => {
        const id = getColumnId(col);
        return id ? ([id, col] as const) : null;
      })
      .filter((v): v is readonly [string, ColumnDef<T, unknown>] => !!v)
  );

  // Order visible columns according to visibleFieldOrder
  const ordered = visibleFieldOrder
    .map((fieldname) => byId.get(fieldname))
    .filter((col): col is ColumnDef<T, unknown> => !!col);

  // Create generic columns for fields in visibleFieldOrder that don't have bespoke columns
  const availableFieldsMap = new Map(
    availableFields.map((f) => [f.fieldname, f])
  );

  const genericColumns: ColumnDef<T, unknown>[] = visibleFieldOrder
    .filter((fieldname) => !byId.has(fieldname))
    .map((fieldname) => {
      const meta = availableFieldsMap.get(fieldname);
      const label = meta?.label || fieldname;
      const fieldtype = meta?.fieldtype || "Data";

      return {
        id: fieldname,
        accessorKey: fieldname,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={label} />
        ),
        cell: ({ row }) => {
          // Use row.original[fieldname] to avoid reliance on TanStack accessors
          const raw = row.original as Record<string, unknown>;
          const value = raw[fieldname];
          
          // Generic rendering based on fieldtype
          if (value === null || value === undefined || value === "") {
            return <span className="text-muted-foreground">-</span>;
          }

          // Check field
          if (fieldtype === "Check") {
            const checked = value === 1 || value === true;
            return <StatusBadge color={checked ? "green" : "gray"}>{checked ? "Yes" : "No"}</StatusBadge>;
          }

          // Date/Datetime fields
          if (fieldtype === "Date") {
            return <div>{formatDate(value as string) || String(value)}</div>;
          }
          if (fieldtype === "Datetime") {
            return <div>{formatDateTime(value as string) || String(value)}</div>;
          }

          // Currency/Float/Int fields
          if (fieldtype === "Currency" || fieldtype === "Float" || fieldtype === "Int") {
            const num = Number(value);
            if (isNaN(num)) return <div>{String(value)}</div>;
            if (fieldtype === "Currency") {
              return <div>{num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>;
            }
            return <div>{num.toLocaleString()}</div>;
          }

          // Link/Select/Data/Text - plain text
          return <div className="truncate max-w-[200px]">{String(value)}</div>;
        },
        enableColumnFilter: false, // v1: keep it simple
        enableSorting: false, // Disable sorting for generic columns to avoid backend ordering issues
        // Future: Could enable sorting only for safe fieldtypes (Int, Float, Currency, Date)
      } as ColumnDef<T, unknown>;
    });

  // Extras: only truly non-configurable columns (e.g. future actions)
  // Only keep non-meta columns - meta-backed fields should only appear if in visibleFieldOrder
  const availableFieldnames = new Set(
    availableFields.map((f) => f.fieldname)
  );

  const extras = baseColumns.filter((col) => {
    const id = getColumnId(col);
    if (!id) return true;
    
    // Don't keep meta-backed fields - they should only appear if in visibleFieldOrder
    const isMetaField = availableFieldnames.has(id);
    if (isMetaField) return false;
    
    // Don't keep anything the user explicitly hid (if it's in visibleFieldOrder, it's already handled above)
    if (visibleFieldOrder.includes(id)) return false;
    
    // Only keep truly non-configurable columns (e.g. future "__actions" column)
    return true;
  });

  // Actions column goes first (right after checkbox in final table), other extras at end
  const actionsExtras = extras.filter((col) => {
    const id = typeof col.id === "string" ? col.id : null;
    return id === "__actions";
  });
  const otherExtras = extras.filter((col) => {
    const id = typeof col.id === "string" ? col.id : null;
    return id !== "__actions";
  });

  return [...actionsExtras, ...ordered, ...genericColumns, ...otherExtras];
}
